import { ReactNode } from 'react';
import { Pencil, Plane } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
    Carousel,
    CarouselContent,
    CarouselItem,
    CarouselNext,
    CarouselPrevious,
} from '@/components/ui/carousel';
import { Progress } from '@/components/ui/progress';
import { UserAvatar } from '@/components/ui/user-avatar';
import { useI18n } from '@/hooks/useI18n';
import { useMoneyCopy } from '@/lib/i18n/money-copy';
import { formatPlanAmount, formatPlanDateShort } from '@/lib/builder/utils/payment-format';
import { PlatformFeeDueBadge } from '@/lib/payments/components/PlatformFeeDueBadge';
import {
    IPaymentsItinerary,
    ItineraryPaymentState,
    itineraryPaymentState,
    paidProgress,
    PeopleDictionary,
    personOf,
} from '@/lib/payments/models/payments-dashboard.model';

interface PaymentsItinerariesCardProps {
    rows: IPaymentsItinerary[];
    /** Anagrafiche citate dalle righe: le righe portano solo la chiave. */
    people: PeopleDictionary;
    isLoading: boolean;
    /** Solo chi puo' gestire il piano vede "modifica": la RPC di salvataggio rifiuterebbe gli altri. */
    canManage: boolean;
    showDesigner: boolean;
    /** Dalla card del viaggio non si parte da una rata: nessuna riga da evidenziare. */
    onEditPlan: (itineraryId: string) => void;
    /** Click sul titolo del viaggio: apre la modale di dettaglio, non il builder. */
    onOpenItinerary: (itineraryId: string) => void;
    /** Impaginazione, montata sotto le righe: la card non sa quante pagine esistono. */
    footer?: ReactNode;
}

const STATE_CLASSES: Record<ItineraryPaymentState, string> = {
    settled: 'bg-green-100 text-green-800 border border-green-300 hover:bg-green-100',
    overdue: 'bg-red-100 text-red-800 border border-red-300 hover:bg-red-100',
    in_progress: 'bg-blue-100 text-blue-800 border border-blue-300 hover:bg-blue-100',
    awaiting_acceptance:
        'bg-amber-100 text-amber-800 border border-amber-300 hover:bg-amber-100',
    draft: 'bg-slate-100 text-slate-700 border border-slate-300 hover:bg-slate-100',
    not_started: 'bg-gray-100 text-gray-600 border border-gray-300 hover:bg-gray-100',
};

/**
 * Cornice della scheda. Tratteggiata per le proposte ancora da accettare: nel carosello
 * il badge si legge solo dopo il titolo, mentre il bordo distingue a colpo d'occhio le
 * due famiglie -- viaggi su cui e' entrato del denaro contro proposte ancora aperte.
 */
const CARD_CLASSES: Partial<Record<ItineraryPaymentState, string>> = {
    awaiting_acceptance: 'border-dashed border-amber-300 bg-amber-50/40',
    // Tratteggiata come la proposta inviata -- sono la stessa famiglia, denaro non ancora
    // in gioco -- ma senza l'ambra dell'attesa: sulla bozza non si aspetta nessuno.
    draft: 'border-dashed border-slate-300 bg-slate-50/60',
};

/**
 * Viaggi con un piano rate e il loro stato di incasso.
 *
 * Il badge NON riflette `itineraries.status`: con un piano attivo l'itinerario passa a
 * "completato" gia' al primo acconto, quindi lo status direbbe che il viaggio e' chiuso
 * mentre restano tre rate da incassare. Lo stato mostrato qui e' quello dei soldi.
 *
 * "Modifica piano" apre la stessa modale dello step Checkout del builder
 * (PaymentPlanEditorModal), senza uscire dalla dashboard: aprire il builder per cambiare
 * una scadenza significherebbe caricare l'intero itinerario e perdere il contesto.
 */
export const PaymentsItinerariesCard = ({
    rows,
    people,
    isLoading,
    canManage,
    showDesigner,
    onEditPlan,
    onOpenItinerary,
    footer,
}: PaymentsItinerariesCardProps) => {
    const { t } = useI18n();
    const money = useMoneyCopy();

    return (
        <Carousel opts={{ align: 'start', slidesToScroll: 'auto' }}>
            <Card>
                <CardHeader className="p-4 pb-3 flex-row items-center justify-between space-y-0">
                    <CardTitle className="text-base font-semibold flex items-center gap-2">
                        <Plane className="w-4 h-4 text-gray-500" />
                        {t('payments.trips.title')}
                    </CardTitle>

                    {rows.length > 0 && (
                        <div className="flex items-center gap-1.5">
                            <CarouselPrevious className="static translate-y-0 h-7 w-7" />
                            <CarouselNext className="static translate-y-0 h-7 w-7" />
                        </div>
                    )}
                </CardHeader>

                <CardContent className="p-4 pt-0">
                    {rows.length === 0 ? (
                        <p className="text-sm text-gray-500">
                            {isLoading
                                ? t('common.loading')
                                : t('payments.trips.empty')}
                        </p>
                    ) : (
                        <CarouselContent className="-ml-3">
                            {rows.map((row) => {
                                const state = itineraryPaymentState(row);
                                const progress = paidProgress(row);

                                return (
                                    <CarouselItem
                                        key={row.itinerary_id}
                                        className="pl-3 basis-full sm:basis-1/2 lg:basis-1/3 xl:basis-1/4 2xl:basis-1/5"
                                    >
                                        <div
                                            className={`h-full rounded-lg border border-gray-200 p-3 flex flex-col gap-2.5 ${CARD_CLASSES[state] ?? ''}`}
                                        >
                                            <div>
                                                <div className="flex items-center gap-2 min-w-0">
                                                    <button
                                                        type="button"
                                                        className="font-medium text-gray-900 hover:underline text-left truncate min-w-0 flex-1"
                                                        title={row.title}
                                                        onClick={() => onOpenItinerary(row.itinerary_id)}
                                                    >
                                                        {row.title}
                                                    </button>
                                                    <Badge
                                                        className={`shrink-0 whitespace-nowrap ${STATE_CLASSES[state]}`}
                                                    >
                                                        {t(`payments.trips.state.${state}`)}
                                                    </Badge>
                                                </div>
                                                {/* Sotto il titolo sta la scadenza, non piu' i nomi: sono passati in fondo
                                                    come icone, e la data e' l'unico dato che chiede un'azione. */}
                                                {/* Sulla proposta ancora da accettare la scadenza della prima rata
                                                    e' una data proposta, non un impegno preso: si mostra la partenza. */}
                                                <span className="block text-xs text-gray-500 truncate">
                                                    {row.next_due_date &&
                                                    !row.all_paid &&
                                                    state !== 'awaiting_acceptance' &&
                                                    state !== 'draft'
                                                        ? t('payments.trips.nextDue', {
                                                              date: formatPlanDateShort(row.next_due_date),
                                                          })
                                                        : row.start_date
                                                          ? formatPlanDateShort(row.start_date)
                                                          : null}
                                                </span>
                                                {/* Un viaggio saldato al 100% puo' avere
                                                    comunque una posizione aperta con noi:
                                                    la barra dice che il cliente ha finito,
                                                    non che abbiamo incassato la fee. */}
                                                <PlatformFeeDueBadge amount={row.platform_fee_due_amount} />
                                            </div>

                                            <Progress value={progress} className="h-1.5" />

                                            <div className="flex gap-3 text-xs mt-auto">
                                                    <span className="flex flex-col leading-tight">
                                                        <span className="text-gray-500">
                                                            {t(`${money}.paid`)}
                                                        </span>
                                                        <span className="text-sm font-semibold text-gray-900 tabular-nums">
                                                            {formatPlanAmount(row.paid_amount)}
                                                        </span>
                                                    </span>
                                                    <span className="flex flex-col leading-tight">
                                                        <span className="text-gray-500">
                                                            {t('payments.trips.remaining')}
                                                        </span>
                                                        <span className="text-sm font-medium text-gray-600 tabular-nums">
                                                            {formatPlanAmount(row.remaining_amount)}
                                                        </span>
                                                    </span>
                                                    {/* Con una rata sola "1 di 1" e' un conteggio
                                                        che non conta nulla: quel piano e' il
                                                        pagamento unico, e quanto sia stato
                                                        incassato lo dicono gia' i due dati
                                                        accanto. */}
                                                    <span className="flex flex-col leading-tight">
                                                        <span className="text-gray-500">
                                                            {row.installments_count === 1
                                                                ? t('payments.trips.paymentType')
                                                                : t('payments.trips.installments')}
                                                        </span>
                                                        <span className="text-sm font-medium text-gray-600 tabular-nums">
                                                            {row.installments_count === 1
                                                                ? t('payments.trips.single')
                                                                : t('payments.due.seqOf', {
                                                                      seq: row.paid_count,
                                                                      total: row.installments_count,
                                                                  })}
                                                        </span>
                                                    </span>
                                            </div>

                                            <div className="flex items-center justify-between gap-2 border-t border-gray-100 pt-2">
                                                {/* Solo i pallini: il nome sta nel tooltip, come nelle tabelle. */}
                                                <div className="flex items-center gap-1.5">
                                                    {(() => {
                                                        const client = personOf(people, row.client_key);
                                                        const designer = personOf(people, row.designer_id);
                                                        return (
                                                            <>
                                                                {client && (
                                                                    <UserAvatar
                                                                        fullName={client.name}
                                                                        email={client.email}
                                                                        imageUrl={client.avatar_url}
                                                                        size="xs"
                                                                        showTooltip
                                                                    />
                                                                )}
                                                                {showDesigner && designer && (
                                                                    <UserAvatar
                                                                        fullName={designer.name}
                                                                        email={designer.email}
                                                                        imageUrl={designer.avatar_url}
                                                                        size="xs"
                                                                        showTooltip
                                                                    />
                                                                )}
                                                            </>
                                                        );
                                                    })()}
                                                </div>

                                                {canManage && (
                                                    <Button
                                                        type="button"
                                                        variant="ghost"
                                                        size="sm"
                                                        className="h-7 w-7 p-0 shrink-0"
                                                        title={t('payments.actions.editPlan')}
                                                        aria-label={t('payments.actions.editPlan')}
                                                        onClick={() => onEditPlan(row.itinerary_id)}
                                                    >
                                                        <Pencil className="w-3.5 h-3.5" />
                                                    </Button>
                                                )}
                                            </div>
                                        </div>
                                    </CarouselItem>
                                );
                            })}
                        </CarouselContent>
                    )}
                    {footer}
                </CardContent>
            </Card>
        </Carousel>
    );
};
